import Phaser from "phaser";
import type { FighterMetadata } from "./Fighter";
import rosterJson from "../data/roster.json";
import type { RosterEntry } from "../data/rosterTypes";

const ROSTER = rosterJson as RosterEntry[];

/**
 * v6: 3 разных CC0 pack'а — renegade / ranger / martial_hero.
 * Каждый pack — отдельный 30-кадровый sprite-sheet 128×192.
 *
 * Студенты roster детерминированно мапятся на один из 3 паков по hash имени —
 * чтобы у каждого был свой стиль, но без необходимости генерить 15 уникальных.
 */

// Базовые архетипы — то что видит игрок в SelectScene (4 пака).
export type BasePackSlug = "boxer_leo" | "boxer_mike" | "boxer_don" | "boxer_raph";
// «Загорелые» варианты — выбираются автоматически для P2 в бою (4 пака).
export type TanPackSlug =
  | "boxer_leo_tan" | "boxer_mike_tan" | "boxer_don_tan" | "boxer_raph_tan";
export type PackSlug = BasePackSlug | TanPackSlug;

export const PACKS: BasePackSlug[] = ["boxer_leo", "boxer_mike", "boxer_don", "boxer_raph"];
export const TAN_PACKS: TanPackSlug[] = ["boxer_leo_tan", "boxer_mike_tan", "boxer_don_tan", "boxer_raph_tan"];
const ALL_PACKS: PackSlug[] = [...PACKS, ...TAN_PACKS];

/** Возвращает «загорелую» версию архетипа для слота P2 (или сам tan-slug если уже tan). */
export function tannedSlug(slug: string): PackSlug {
  if (slug.endsWith("_tan")) return slug as PackSlug;
  const candidate = `${slug}_tan` as PackSlug;
  return TAN_PACKS.includes(candidate as TanPackSlug) ? candidate : (slug as PackSlug);
}

export function packFor(slug: string): PackSlug {
  if (ALL_PACKS.includes(slug as PackSlug)) return slug as PackSlug;
  // Для произвольных slug (roster студентов, login друзей) — детерминированный mapping
  let h = 0;
  for (let i = 0; i < slug.length; i++) h = (h * 31 + slug.charCodeAt(i)) | 0;
  return PACKS[Math.abs(h) % PACKS.length];
}

export function spriteKeyFor(slug: string): string {
  return `fighter:${packFor(slug)}`;
}

export function preloadFighter(scene: Phaser.Scene, slug: string) {
  const pack = packFor(slug);
  const key = `fighter:${pack}`;
  const metaKey = `fighter-meta:${pack}`;
  if (!scene.textures.exists(key) && !scene.load.list.entries.find((e: any) => e.key === key)) {
    scene.load.spritesheet(key, `assets/fighters/${pack}.png`,
      { frameWidth: 128, frameHeight: 192 });
  }
  if (!scene.cache.json.has(metaKey) && !scene.load.list.entries.find((e: any) => e.key === metaKey)) {
    scene.load.json(metaKey, `assets/fighters/${pack}.json`);
  }
}

export function preloadAllPacks(scene: Phaser.Scene) {
  for (const p of ALL_PACKS) preloadFighter(scene, p);
}

export function getFighterMeta(scene: Phaser.Scene, slug: string): FighterMetadata {
  const pack = packFor(slug);
  const data = scene.cache.json.get(`fighter-meta:${pack}`);
  if (!data) throw new Error(`Pack meta '${pack}' not loaded for slug ${slug}`);
  const rosterEntry = ROSTER.find(r => r.slug === slug);
  return {
    ...data,
    slug,
    name: rosterEntry?.name ?? data.name ?? slug,
  } as FighterMetadata;
}

export const PACK_LABELS: Record<PackSlug, string> = {
  boxer_leo:      "ЛЕО",
  boxer_mike:     "МАЙК",
  boxer_don:      "ДОН",
  boxer_raph:     "РАФ",
  boxer_leo_tan:  "ЛЕО",
  boxer_mike_tan: "МАЙК",
  boxer_don_tan:  "ДОН",
  boxer_raph_tan: "РАФ",
};
